import { Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Store } from '@ngrx/store';
import { AppState } from '../../../../../../../../store';
import { StoreItemActions } from '../../../../../../../../store/entities/store-item/store-item.actions';
import { StoreItemEntity } from '../../../../../../../../store/entities/store-item/store-item.model';
import {
  CREATE_ZONES_BINS_MAPPING,
  UPDATE_ZONES_BINS_MAPPING
} from "../../../../../../../../store/entities/warehouse-management/bin/bin.graphql";

@Component({
  selector: 'app-bin-add',
  templateUrl: './bin-add.component.html',
  styleUrl: './bin-add.component.scss'
})
export class BinAddComponent implements OnInit {
  form: FormGroup;
  title: string = "Add Zone Bin";
  loading: boolean = false;
  mode: 'upload' | 'create';

  constructor(
      private fb: FormBuilder,
      private store: Store<AppState>,
      private dialogRef: MatDialogRef<BinAddComponent>,
      @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.mode = data?.mode || 'upload';
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  private initializeForm() {
    this.form = this.fb.group({
      csvFile: [null], // File Upload
      binNumbers: this.fb.array([]) // Manually added bins
    });

    if (this.mode === 'create') {
      if (this.data?.binNumber) {
        // Ensure binNumber is always an array
        const bins = typeof this.data.binNumber === 'string' ? [this.data.binNumber] : this.data.binNumber;
        this.setExistingBins(bins);
      } else {
        this.addBin(); // Ensure at least one bin input exists
      }
    }
  }

  // Getters for template access
  get binNumbers(): FormArray {
    return this.form.get('binNumbers') as FormArray;
  }

  addBin(value: string = '') {
    this.binNumbers.push(this.fb.control(value, Validators.required));
  }

  removeBin(index: number) {
    if (this.binNumbers.length > 1) {
      this.binNumbers.removeAt(index);
    }
  }

  setExistingBins(bins: string[]) {
    this.binNumbers.clear(); // Clear existing values
    bins.forEach(bin => this.addBin(bin.trim())); // Populate the form array with existing bins
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        this.form.patchValue({ csvFile: result });
      };
      reader.readAsText(file);
    }
  }

  submitForm() {
    if (this.form.invalid) return;

    this.loading = true;
    let binData = this.mode === 'upload' ? this.processCSVFile() : this.binNumbers.value;

    this.store.dispatch(StoreItemActions.createUpdateStoreItem({
      entity: StoreItemEntity.zoneBinCoverage,
      mutation: this.data?.uniqueId ? UPDATE_ZONES_BINS_MAPPING : CREATE_ZONES_BINS_MAPPING,
      isInput: true,
      input: {
        uniqueId: this.data?.uniqueId,
        zoneUniqueId: this.data?.zoneUniqueId,
        binNumbers: binData
      }
    }));

    this.loading = false;
    this.closeDialog();
  }

  private processCSVFile(): string[] {
    const fileContent = this.form.get('csvFile')?.value;
    if (!fileContent) return [];

    return this.parseBinNumbersFromCSV(fileContent);
  }

  private parseBinNumbersFromCSV(csv: string): string[] {
    const lines = csv.trim().split("\n");
    if (lines.length < 2) return [];

    const headers = lines[0].split(",").map(h => h.replace(/^"|"$/g, "").trim());
    const binNumberIdx = headers.findIndex(h => h === "binNumber");
    if (binNumberIdx === -1) {
      console.warn("No 'binNumber' column found in CSV");
      return [];
    }

    return lines.slice(1).map(row => {
      const rowParts = row.split(",").map(part => part.replace(/"/g, "").trim());
      return rowParts[binNumberIdx] || null;
    }).filter(Boolean);
  }

  closeDialog() {
    this.dialogRef.close();
  }
}